import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { api, imgSrc } from "@/lib/api";

export function BlogCards({ posts, base }) {
  if (!posts.length) return <p className="mt-12 text-ink2">Stories are simmering — check back soon.</p>;
  return (
    <div className="mt-12 grid gap-8 md:grid-cols-2 lg:grid-cols-3">
      {posts.map((post, i) => (
        <motion.article
          key={post.id}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: i * 0.08 }}
          className="group overflow-hidden rounded-sm border border-brass/10 bg-white"
          data-testid={`blog-card-${post.slug}`}
        >
          {post.image && (
            <div className="overflow-hidden">
              <img src={imgSrc(post.image)} alt={post.title} className="aspect-[16/9] w-full object-cover transition-transform duration-700 group-hover:scale-105 max-h-[80vh]" />
            </div>
          )}
          <div className="p-6">
            <p className="text-[10px] font-mono uppercase tracking-[0.25em] text-flame/80">{new Date(post.created_at).toLocaleDateString("en-US", { month: "long", year: "numeric" })}</p>
            <h3 className="mt-2 font-serif text-xl text-ink">{post.title}</h3>
            <p className="mt-2 text-sm text-ink2 leading-relaxed line-clamp-3">{post.excerpt}</p>
            <Link to={`${base}/journal/${post.slug}`} data-testid={`blog-read-${post.slug}`} className="mt-4 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-gold transition-colors hover:text-flame">
              Read <ArrowUpRight size={14} />
            </Link>
          </div>
        </motion.article>
      ))}
    </div>
  );
}

export default function BlogSection({ section, env, base = "", preview = false }) {
  const [posts, setPosts] = useState([]);
  useEffect(() => {
    api.get("/blog", { params: { env } }).then((r) => setPosts(r.data.posts || [])).catch(() => {});
  }, [env]);
  const shown = preview ? posts.slice(0, 3) : posts;
  return (
    <section className="mx-auto max-w-7xl px-6 sm:px-12 lg:px-20 py-24 sm:py-32" data-testid={preview ? "blog-preview-section" : "blog-list-section"}>
      <p className="text-xs uppercase font-mono tracking-[0.3em] text-gold">{section.eyebrow}</p>
      <h2 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold text-ink tracking-tight">{section.title}</h2>
      <BlogCards posts={shown} base={base} />
    </section>
  );
}
